import { AfterViewInit, ChangeDetectorRef, Directive, Input, OnInit, Renderer2 } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { difference, pull, toArray } from 'lodash-es';
import { fromEvent as observableFromEvent, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Extension to MatSelect, which applies a class
 * to the displayed value.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[matExtSelectFilter]'
})
export class MatSelectFilterExtensionDirective implements OnInit, AfterViewInit {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input() selectedClass: string;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('mat-ext-overlay-panel-class') set _(value: string) {
    this.host._overlayPanelClass = 'mat-ext-select-filter-panel';
  }

  private filterInput: any;
  private filterInputWrap: any;
  private filterText$: Observable<string>;
  private getSelectPanel: any = () => this.host.panel.nativeElement;

  constructor(private host: MatSelect, private renderer: Renderer2, private cd: ChangeDetectorRef) {
    // Create filter input;
    const filterInputEl = this.renderer.createElement('input');
    this.renderer.setAttribute(filterInputEl, 'type', 'text');
    this.renderer.setAttribute(filterInputEl, 'placeholder', 'Search…');
    this.renderer.setProperty(filterInputEl, 'value', '');
    this.renderer.setAttribute(filterInputEl, 'data-test-id', 'matSelectFilterInput');
    this.renderer.addClass(filterInputEl, 'ab-form-input');

    // Set filter input
    this.filterInput = filterInputEl;

    // Create filter input wrapper
    const filterInputElWrap = this.renderer.createElement('div');
    this.renderer.addClass(filterInputElWrap, 'mat-filter-input-wrap');
    this.renderer.addClass(filterInputElWrap, 'd-none');

    // Place filterInputEl inside filterInputElWrap
    this.renderer.appendChild(filterInputElWrap, filterInputEl);

    // Set filter input wrap
    this.filterInputWrap = filterInputElWrap;

    // Create / set observable
    this.filterText$ = observableFromEvent(filterInputEl, 'keyup')
      .pipe(
        map((event: KeyboardEvent) => event.target['value'] as string)
      );
  }

  ngOnInit() {
    // subscribe to observable
    this.filterText$.pipe(
      map(filterText => (filterText || '').toLowerCase()),
      map(filterText => {
        let visible = [];
        let hidden = [];
        const optionGroupsEls = toArray(this.getSelectPanel().querySelectorAll('mat-optgroup'));
        const optionEls = toArray(this.getSelectPanel().querySelectorAll('mat-option'));

        let visibleOptions = optionEls.filter(el => el.innerText.toLowerCase().includes(filterText) === true);
        const hiddenOptions = optionEls.filter(el => el.innerText.toLowerCase().includes(filterText) === false);
        if (optionGroupsEls.length) {
          // Add all options to visible if optgroup name matches filter
          optionGroupsEls.forEach(el => {
            const optGroupName = el.querySelector('label').innerText.toLowerCase();
            if (optGroupName.includes(filterText)) {
              const childOpts = toArray(el.querySelectorAll('mat-option'));
              // remove from hidden options
              pull(hiddenOptions, ...childOpts);
              // push to visible options
              visibleOptions = visibleOptions.concat(childOpts);
              // push group to visible
              visible.push(el);
            }
          });
          // Hide optgroups that have no visible children
          optionGroupsEls.forEach(el => {
            const childOpts = toArray(el.querySelectorAll('mat-option'));
            const stillVisible = difference(childOpts, hiddenOptions);
            if (stillVisible.length === 0) {
              // push group to hidden
              hidden.push(el);
            }
          });

          // Set visible and hidden elements
          hidden = hidden.concat(hiddenOptions);
          visible = visible.concat(visibleOptions);
        } else {
          visible = visibleOptions;
          hidden = hiddenOptions;
        }

        // returns array of elements to hide and elements to show
        return {
          visible,
          hidden
        };
      })
    ).
      subscribe(({ hidden, visible }) => {
        // Hide Elements
        hidden.forEach(el => this.renderer.addClass(el, 'mat-select-filter-modified'));
        hidden.forEach(el => this.renderer.addClass(el, 'd-none'));
        // Show elements
        visible.forEach(el => this.renderer.removeClass(el, 'mat-select-filter-modified'));
        visible.forEach(el => this.renderer.removeClass(el, 'd-none'));
      });
  }

  ngAfterViewInit() {
    this.host.openedChange
      .subscribe(isOpened => {
        if (isOpened) {
          const selectPanel = this.getSelectPanel();
          this.renderer.insertBefore(selectPanel, this.filterInputWrap, selectPanel.firstChild);
          this.renderer.addClass(selectPanel, 'has-mat-filter-input-wrap');
          // note: may go with the following solution, but it'll require consiberably more css work
          // it adds the filter input above/before the scroll list (rather than within it),
          // so no need for `position: sticky`, but more need to (re)move drop shadows, change how
          // cdk-overlay-pane renders in this particular case (to `display: block`), etc, etc.
          selectPanel.before(this.filterInputWrap);
          this.renderer.removeClass(this.filterInputWrap, 'd-none');
        } else {
          this.renderer.addClass(this.filterInputWrap, 'd-none');
          this.renderer.setProperty(this.filterInput, 'value', '');
          document.querySelectorAll('.mat-select-filter-modified').forEach(el => {
            this.renderer.removeClass(el, 'd-none');
            this.renderer.removeClass(el, 'mat-select-filter-modified');
          });
          this.cd.markForCheck();
        }
      });
  }
}
